import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetBandobastDetailsQuery } from "../../services/appln";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import strings from "../../config/localization";
import Typography from "@mui/material/Typography";
import ProgressIndicator from "../../components/ProgressIndicator";
import { DataGrid } from "@mui/x-data-grid";
import { convertDate, convertTimestamp, getLocationStrMap } from "../util";
import { Desc, Subtitle, Title, Subsubtitle } from "../../components/Desc";
import { getBaseURL } from "../constants";

const personnelAssessmentColumns = [
  {
    field: "designationName",
    headerName: strings.designation,
    width: 300,
  },
  {
    field: "count",
    headerName: strings.assessmentCount,
    width: 150,
  },
];

const personnelAllocationColumns = [
  {
    field: "personnelName",
    headerName: strings.name,
    width: 225,
  },
  {
    field: "designationName",
    headerName: strings.designation,
    width: 225,
  },
];

const resourceAssessmentColumns = [
  {
    field: "resourceTypeName",
    headerName: strings.resourceType,
    width: 300,
  },
  {
    field: "count",
    headerName: strings.assessmentCount,
    width: 150,
  },
];

const resourceAllocationColumns = [
  {
    field: "resourceExternalSerialNo",
    headerName: strings.serialNo,
    width: 225,
  },
  {
    field: "resourceTypeName",
    headerName: strings.resourceType,
    width: 225,
  },
];

const ShowLocation = ({
  location,
  allPersonnelAssessments,
  allPersonnelAllocations,
  allResourceAssessments,
  allResourceAllocations,
}: any) => {
  const locationMap = getLocationStrMap();
  const personnelAssessments = allPersonnelAssessments.filter(
    (e: any) => e.locationId === location.id,
  );
  const personnelAllocations = allPersonnelAllocations.filter(
    (e: any) => e.locationId === location.id,
  );
  const resourceAssessments = allResourceAssessments.filter(
    (e: any) => e.locationId === location.id,
  );
  const resourceAllocations = allResourceAllocations.filter(
    (e: any) => e.locationId === location.id,
  );
  return (
    <Stack spacing={1}>
      <Stack spacing={0}>
        <Desc label={strings.name} value={location.name} />
        <Desc label={strings.kind} value={locationMap.get(location.kind)!} />
      </Stack>
      <Stack spacing={0}>
        <Typography variant="subtitle2">
          {strings.personnelAssessments}
        </Typography>
        {personnelAssessments.length > 0 ? (
          <Grid container spacing={1}>
            <Grid item xs={10}>
              <DataGrid
                rowHeight={30}
                rows={personnelAssessments}
                columns={personnelAssessmentColumns}
                disableRowSelectionOnClick
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">{strings.none}</Typography>
        )}
      </Stack>
      <Stack spacing={0}>
        <Typography variant="subtitle2">
          {strings.personnelAllocation}
        </Typography>
        {personnelAllocations.length > 0 ? (
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <DataGrid
                rowHeight={30}
                rows={personnelAllocations}
                columns={personnelAllocationColumns}
                disableRowSelectionOnClick
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">{strings.none}</Typography>
        )}
      </Stack>
      <Stack spacing={0}>
        <Typography variant="subtitle2">
          {strings.resourceAssessments}
        </Typography>
        {resourceAssessments.length > 0 ? (
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <DataGrid
                rowHeight={30}
                rows={resourceAssessments}
                columns={resourceAssessmentColumns}
                disableRowSelectionOnClick
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">{strings.none}</Typography>
        )}
      </Stack>
      <Stack spacing={0}>
        <Typography variant="subtitle2">
          {strings.resourceAllocation}
        </Typography>
        {resourceAllocations.length > 0 ? (
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <DataGrid
                rowHeight={30}
                rows={resourceAllocations}
                columns={resourceAllocationColumns}
                disableRowSelectionOnClick
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1">{strings.none}</Typography>
        )}
      </Stack>
    </Stack>
  );
};

const ShowTimeslot = ({
  timeslot,
  allLocations,
  allPersonnelAssessments,
  allPersonnelAllocations,
  allResourceAssessments,
  allResourceAllocations,
}: any) => {
  const locations = allLocations.filter(
    (location: any) => location.timeslotId === timeslot.id,
  );
  return (
    <>
      <Stack spacing={0}>
        <Desc label={strings.name} value={timeslot.name} />
        <Desc label={strings.description} value={timeslot.desc} />
        <Desc
          label={strings.startTime}
          value={convertTimestamp(timeslot.startTime)}
        />
        <Desc
          label={strings.endTime}
          value={convertTimestamp(timeslot.endTime)}
        />
      </Stack>
      <Stack spacing={0}>
        <Subsubtitle text={strings.locations} />
        <Stack spacing={2}>
          {locations.map((location: any) => (
            <ShowLocation
              location={location}
              allPersonnelAssessments={allPersonnelAssessments}
              allPersonnelAllocations={allPersonnelAllocations}
              allResourceAssessments={allResourceAssessments}
              allResourceAllocations={allResourceAllocations}
            />
          ))}
        </Stack>
      </Stack>
    </>
  );
};

export default function BandobastDetails() {
  const params = useParams();
  const navigate = useNavigate();

  const bandobastQueryResponse = useGetBandobastDetailsQuery(
    params.bandobastId,
  );
  console.log(bandobastQueryResponse.data);

  const details = bandobastQueryResponse?.data;

  if (
    bandobastQueryResponse.isLoading ||
    bandobastQueryResponse.isFetching ||
    !details
  ) {
    return (
      <Box sx={{ flexGrow: 1, width: "100%" }}>
        <Title text={strings.details} />
        <ProgressIndicator />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, width: "100%" }}>
      <Title text={strings.details} />
      <Stack spacing={4}>
        <Stack spacing={0}>
          <Desc label={strings.serialNo} value={details.serialNo} />
          <Desc label={strings.name} value={details.name} />
          <Desc
            label={strings.startDate}
            value={convertDate(details.startDate)}
          />
          <Desc label={strings.endDate} value={convertDate(details.endDate)} />
          <Desc label={strings.place} value={convertDate(details.place)} />
          <Desc label={strings.heading} value={convertDate(details.heading)} />
          <Desc
            label={strings.subHeading}
            value={convertDate(details.subHeading)}
          />
          <Desc
            label={strings.supervisorName}
            value={convertDate(details.supervisorName)}
          />
          <Desc
            label={strings.supervisorMobile}
            value={convertDate(details.supervisorMobile)}
          />
          <img
            src={`${getBaseURL()}/bandobast/${details.id}/getLeftLogo`}
            height={100}
            width={100}
            alt={""}
          />
          <img
            src={`${getBaseURL()}/bandobast/${details.id}/getRightLogo`}
            height={100}
            width={100}
            alt={""}
          />
          <img
            src={`${getBaseURL()}/bandobast/${details.id}/getSignature`}
            height={100}
            width={100}
            alt={""}
          />
        </Stack>

        <Stack spacing={1}>
          <Subtitle text={strings.timeslots} />
          <Stack spacing={3}>
            {details.timeslots.map((timeslot: any) => (
              <ShowTimeslot
                timeslot={timeslot}
                allLocations={details.locations}
                allPersonnelAssessments={details.personnelAssessments}
                allPersonnelAllocations={details.personnelAllocations}
                allResourceAssessments={details.resourceAssessments}
                allResourceAllocations={details.resourceAllocations}
              />
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
